/**
 * MyPodOps Studio V2 - Main Entry Point
 *
 * Initializes the React application with:
 * - React 18 concurrent features
 * - CSS styles
 * - Error boundary for crash recovery
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './styles/studio.css';

// Error boundary for production crashes
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[Studio] Critical error:', error, errorInfo);

    // Track error in analytics
    if (window.studioAnalytics) {
      window.studioAnalytics.trackEvent('studio_error', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-state" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#0d0d0d',
          color: '#ffffff',
          padding: '20px',
          textAlign: 'center'
        }}>
          <span style={{ fontSize: '64px', marginBottom: '24px' }}>😵</span>
          <h1 style={{ marginBottom: '16px' }}>Something went wrong</h1>
          <p style={{ color: '#b3b3b3', marginBottom: '24px', maxWidth: '400px' }}>
            The studio encountered an unexpected error. Please refresh the page to try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #2CC5FF 0%, #6D1A86 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Refresh Page
          </button>
          {this.state.error && (
            <details style={{ marginTop: '24px', color: '#666666', maxWidth: '600px' }}>
              <summary style={{ cursor: 'pointer' }}>Technical details</summary>
              <pre style={{
                marginTop: '12px',
                padding: '12px',
                background: '#1a1a1a',
                borderRadius: '8px',
                overflow: 'auto',
                textAlign: 'left',
                fontSize: '12px'
              }}>
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// Extend Window interface for analytics
declare global {
  interface Window {
    studioAnalytics?: {
      trackEvent: (event: string, data?: Record<string, unknown>) => void;
    };
  }
}

// Get root element
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found. Make sure index.html has a div with id="root"');
}

// Create React 18 root with concurrent features
const root = ReactDOM.createRoot(rootElement);

// Render app with error boundary
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

// Remove loading screen once app is mounted
const loadingScreen = document.querySelector('.loading-screen');
if (loadingScreen) {
  loadingScreen.remove();
}

// Log startup
console.log('[Studio] MyPodOps Studio V2 initialized');
